'use client';

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { decodeJwtPayload } from '@/lib/jwt-utils';

/**
 * 앱 시작 시 인증 상태를 확인하고 초기화하는 컴포넌트
 * localStorage에서 토큰을 읽어 인증 상태를 복원합니다.
 * 주기적으로 토큰을 갱신하여 로그인 상태를 유지합니다.
 */
export function AuthInitializer() {
  const { setAuth, clearAuth, setLoading, refreshTokens, updateUser } = useAuthStore();
  const refreshIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // localStorage에서 토큰 읽기
        const accessToken = localStorage.getItem('accessToken');
        const idToken = localStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('refreshToken');

        if (!accessToken || !idToken) {
          console.log('[AuthInitializer] 토큰이 없음 - 로그아웃 상태로 설정');
          clearAuth();
          return;
        }

        // ID 토큰에서 사용자 정보 및 그룹 정보 추출
        const idTokenPayload = decodeJwtPayload(idToken);

        // 토큰 만료 확인
        const currentTime = Math.floor(Date.now() / 1000);
        const tokenExp = idTokenPayload.exp as number | undefined;
        const tokenExpired = tokenExp && tokenExp < currentTime;

        if (tokenExpired) {
          // 토큰 만료됨 - refreshToken으로 갱신 시도
          console.log('[AuthInitializer] 토큰이 만료됨 - refreshToken으로 갱신 시도');

          if (!refreshToken) {
            console.log('[AuthInitializer] refreshToken이 없음 - 로그아웃 상태로 설정');
            clearAuth();
            return;
          }

          // 먼저 사용자 정보를 임시로 복원 (refreshTokens 함수에서 username이 필요)
          setAuth(
            {
              userId: idTokenPayload.sub as string,
              username: idTokenPayload['cognito:username'] as string,
              email: idTokenPayload.email as string | undefined,
              name: idTokenPayload.name as string | undefined,
              groups: [],
            },
            accessToken,
            idToken,
            refreshToken
          );

          // refreshToken으로 새 토큰 받기
          const refreshSuccess = await refreshTokens();

          if (!refreshSuccess) {
            console.log('[AuthInitializer] 토큰 갱신 실패 - 로그아웃 상태로 설정');
            clearAuth();
            return;
          }

          console.log('[AuthInitializer] 토큰 갱신 성공');

          // /api/auth/me를 호출하여 DynamoDB에서 저장된 사용자 이름 가져오기
          try {
            const meResponse = await fetch('/api/auth/me', {
              method: 'GET',
              credentials: 'include',
            });

            if (meResponse.ok) {
              const meData = await meResponse.json();
              if (meData.success && meData.data) {
                // 저장된 이름과 username으로 업데이트 (일관성 확보)
                const updateData: { name?: string; username?: string } = {};
                if (meData.data.name) {
                  updateData.name = meData.data.name;
                }
                if (meData.data.username) {
                  updateData.username = meData.data.username;
                }
                if (Object.keys(updateData).length > 0) {
                  updateUser(updateData);
                  console.log('[AuthInitializer] 사용자 이름 업데이트:', updateData);
                }
              }
            }
          } catch (meError) {
            console.error('[AuthInitializer] 사용자 정보 조회 실패:', meError);
            // 실패해도 계속 진행
          }
        } else {
          // 토큰이 유효함 - 그룹 정보 추출
          let groups: string[] = [];

          // Lambda trigger에서 추가한 custom:groups 클레임 확인
          if (idTokenPayload['custom:groups']) {
            try {
              groups = JSON.parse(idTokenPayload['custom:groups'] as string);
            } catch {
              groups = [];
            }
          }

          // 또는 cognito:groups 클레임 확인 (기본 Cognito 그룹)
          if (!groups.length && idTokenPayload['cognito:groups']) {
            groups = idTokenPayload['cognito:groups'] as string[];
          }

          console.log('[AuthInitializer] 인증 상태 복원 성공:', {
            username: idTokenPayload['cognito:username'],
            groups,
          });

          // 사용자 정보 복원
          setAuth(
            {
              userId: idTokenPayload.sub as string,
              username: idTokenPayload['cognito:username'] as string,
              email: idTokenPayload.email as string | undefined,
              name: idTokenPayload.name as string | undefined,
              groups: groups,
            },
            accessToken,
            idToken,
            refreshToken || undefined
          );

          // /api/auth/me를 호출하여 DynamoDB에서 저장된 사용자 이름 가져오기
          try {
            const meResponse = await fetch('/api/auth/me', {
              method: 'GET',
              credentials: 'include',
            });

            if (meResponse.ok) {
              const meData = await meResponse.json();
              if (meData.success && meData.data) {
                // 저장된 이름과 username으로 업데이트 (일관성 확보)
                const updateData: { name?: string; username?: string } = {};
                if (meData.data.name) {
                  updateData.name = meData.data.name;
                }
                if (meData.data.username) {
                  updateData.username = meData.data.username;
                }
                if (Object.keys(updateData).length > 0) {
                  updateUser(updateData);
                  console.log('[AuthInitializer] 사용자 이름 업데이트:', updateData);
                }
              }
            }
          } catch (meError) {
            console.error('[AuthInitializer] 사용자 정보 조회 실패:', meError);
            // 실패해도 계속 진행
          }

          // 토큰 만료 5분 전에 자동 갱신하도록 타이머 설정
          const expiresIn = (tokenExp || 0) - currentTime;
          const refreshTime = Math.max(0, (expiresIn - 5 * 60) * 1000); // 만료 5분 전 (밀리초)

          console.log(`[AuthInitializer] ${Math.floor(refreshTime / 1000)}초 후 자동 토큰 갱신 예정`);

          if (refreshIntervalRef.current) {
            clearTimeout(refreshIntervalRef.current);
          }

          refreshIntervalRef.current = setTimeout(async () => {
            console.log('[AuthInitializer] 자동 토큰 갱신 시작');
            const success = await refreshTokens();
            if (!success) {
              console.error('[AuthInitializer] 자동 토큰 갱신 실패');
              clearAuth();
            }
          }, refreshTime);
        }
      } catch (error) {
        console.error('[AuthInitializer] 인증 상태 복원 실패:', error);
        clearAuth();
      } finally {
        // 항상 로딩 상태 해제
        setLoading(false);
      }
    };

    checkAuth();

    // 클린업: 타이머 정리
    return () => {
      if (refreshIntervalRef.current) {
        clearTimeout(refreshIntervalRef.current);
      }
    };
  }, [setAuth, clearAuth, setLoading, refreshTokens, updateUser]);

  return null;
}